import { parseDesc } from '../hooks/useEventos';
import { normISO } from './surgeryHelpers';

/* ── Helpers ──────────────────────────────────────────────── */

const fmtHora  = (iso) => {
  if (!iso) return '';
  const d = new Date(normISO(iso));
  return isNaN(d) ? '' : d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const fmtFecha = (iso) => {
  if (!iso) return '';
  const d = new Date(normISO(iso));
  return isNaN(d) ? '' : d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const clean = (v) =>
  (!v || v === 'NA' || v === 'No se cuenta con la información' || v === 'No se requiere')
    ? ''
    : v.trim();

/* Texto libre del "Requerimiento especial" (checkbox ¿Requiere de algún Material
   o Equipo especial?). Se guarda en la descripción (RequerimientoEspecial=...) al
   reservar, y en event.requerimientoEspecial al reagendar — misma prioridad que
   usa la Agenda al mostrarlo. */
const textoRequerimientoEspecial = (doc) => {
  const ev = doc?.event || {};
  const fields = parseDesc(ev.description);
  const v = ev.requerimientoEspecial || fields['RequerimientoEspecial'];
  return typeof v === 'string' ? clean(v) : '';
};

/* Requerimientos especiales → string legible */
const fmtReqs = (doc) => {
  const ev  = doc?.event || {};
  const reqs = [];
  const img  = ev.imagenologia;
  const mue  = ev.muestrapatologica;

  if (img && typeof img === 'object') {
    if (img.arcoC)  reqs.push('Arco C');
    if (img.rayoxX) reqs.push('Rayos X');
  }
  if (Array.isArray(ev.materialEspecial) && ev.materialEspecial.length > 0)
    reqs.push('Material especial');
  if (Array.isArray(ev.rentaEquipos) && ev.rentaEquipos.length > 0)
    reqs.push('Renta de equipos');
  if (mue && typeof mue === 'object') {
    if (mue.postOperatorios) reqs.push('Muestra post-op.');
    if (mue.trasOperatorios) reqs.push('Muestra trans-op.');
  }
  if (textoRequerimientoEspecial(doc)) reqs.push('Requerimiento especial');
  return reqs.join(' | ') || 'Sin requerimientos';
};

/* Columnas del reporte */
const COLUMNAS = [
  'Fecha',
  'Sala',
  'H. Inicio',
  'H. Final',
  'Médico',
  'Paciente',
  'Edad',
  'Sexo',
  'Procedimiento',
  'Diagnóstico',
  'Tipo de atención',
  'Anestesiólogo',
  'Tipo anestesia',
  'Requerimientos especiales',
  'Detalle requerimiento especial',
  'Estatus',
  'Solicitado por',
];

/* Convierte un doc de Firestore a una fila del reporte */
const docToFila = (doc) => {
  const ev     = doc?.event || {};
  const fields = parseDesc(ev.description);

  return [
    fmtFecha(ev.start?.dateTime),              // Fecha
    clean(fields['AppointmentType']),           // Sala
    fmtHora(ev.start?.dateTime),               // H. Inicio
    fmtHora(ev.end?.dateTime),                 // H. Final
    clean(fields['Medico']),                   // Médico
    clean(fields['Paciente']),                 // Paciente
    clean(fields['EdadPaciente']),             // Edad
    clean(fields['SexoPaciente']),             // Sexo
    clean(fields['Procedimiento']),            // Procedimiento
    clean(fields['Diagnostico']),              // Diagnóstico
    clean(fields['TipoCliente'] || fields['TipoClienteOpcion']), // Tipo de atención
    clean(fields['Anestesiologo']),            // Anestesiólogo
    clean(fields['TipoAnestesia']),            // Tipo anestesia
    fmtReqs(doc),                             // Requerimientos especiales
    textoRequerimientoEspecial(doc),          // Detalle requerimiento especial
    ev.summary || '',                         // Estatus
    clean(fields['Solicitado']),              // Solicitado por
  ];
};

/* ── Exportar CSV ─────────────────────────────────────────── */
export const exportarCSV = (docs, nombreArchivo) => {
  const filas   = [COLUMNAS, ...docs.map(docToFila)];
  const csvStr  = filas
    .map(fila =>
      fila.map(celda => {
        const str = String(celda ?? '');
        // Escapar celdas con comas, saltos de línea o comillas
        return str.includes(',') || str.includes('"') || str.includes('\n')
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      }).join(',')
    )
    .join('\n');

  const blob = new Blob(['\uFEFF' + csvStr], { type: 'text/csv;charset=utf-8;' });
  descargar(blob, `${nombreArchivo}.csv`);
};

/* ── Exportar XLSX usando xlsx-js-style (cargado desde CDN si no está disponible) ──
   Nota: se usa "xlsx-js-style" en vez del paquete "xlsx" estándar (SheetJS
   Community Edition) porque este último NO escribe estilos de celda
   (colores, fuentes) al generar el archivo — solo los lee. xlsx-js-style
   expone la misma API (XLSX.*) pero sí soporta escritura de estilos. */
export const exportarXLSX = async (docs, nombreArchivo, rangoDesde, rangoHasta) => {
  // Cargar xlsx-js-style dinámicamente si no está en el bundle
  let XLSX = window.XLSX;
  if (!XLSX) {
    await new Promise((res, rej) => {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/xlsx-js-style/dist/xlsx.bundle.js';
      s.onload = res;
      s.onerror = rej;
      document.head.appendChild(s);
    });
    XLSX = window.XLSX;
  }

  const filas = [COLUMNAS, ...docs.map(docToFila)];
  const wb    = XLSX.utils.book_new();
  const ws    = XLSX.utils.aoa_to_sheet(filas);

  /* Ancho de columnas */
  ws['!cols'] = [
    { wch: 12 }, // Fecha
    { wch: 32 }, // Sala
    { wch: 10 }, // H. Inicio
    { wch: 10 }, // H. Final
    { wch: 28 }, // Médico
    { wch: 24 }, // Paciente
    { wch: 14 }, // Edad
    { wch: 10 }, // Sexo
    { wch: 36 }, // Procedimiento
    { wch: 30 }, // Diagnóstico
    { wch: 16 }, // Tipo de atención
    { wch: 28 }, // Anestesiólogo
    { wch: 14 }, // Tipo anestesia
    { wch: 36 }, // Requerimientos
    { wch: 60 }, // Detalle requerimiento especial
    { wch: 12 }, // Estatus
    { wch: 28 }, // Solicitado por
  ];

  /* Estilo de la fila de encabezado */
  const headerRange = XLSX.utils.decode_range(ws['!ref']);
  for (let c = headerRange.s.c; c <= headerRange.e.c; c++) {
    const cellAddr = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[cellAddr]) {
      ws[cellAddr].s = {
        font      : { bold: true, color: { rgb: '374151' } },
        fill      : { fgColor: { rgb: 'E5E7EB' } },
        alignment : { horizontal: 'center' },
      };
    }
  }

  /* Columna de detalle con ajuste de texto (textos largos) */
  const colDetalle = COLUMNAS.indexOf('Detalle requerimiento especial');
  for (let r = 1; r <= headerRange.e.r; r++) {
    const addr = XLSX.utils.encode_cell({ r, c: colDetalle });
    if (ws[addr]) ws[addr].s = { alignment: { wrapText: true, vertical: 'top' } };
  }

  /* Filtro (autofiltro) en la fila de encabezado, sobre todo el rango de datos */
  ws['!autofilter'] = { ref: ws['!ref'] };

  /* Hoja de metadatos del reporte */
  const wsMeta = XLSX.utils.aoa_to_sheet([
    ['Reporte de Agenda Quirúrgica'],
    ['Generado el:', new Date().toLocaleDateString('es-MX')],
    ['Rango:', `${rangoDesde || '—'} al ${rangoHasta || '—'}`],
    ['Total de registros:', docs.length],
  ]);

  XLSX.utils.book_append_sheet(wb, wsMeta, 'Info');
  XLSX.utils.book_append_sheet(wb, ws, 'Reporte');

  XLSX.writeFile(wb, `${nombreArchivo}.xlsx`);
};

/* ── Descarga genérica ────────────────────────────────────── */
const descargar = (blob, nombreArchivo) => {
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href  = url;
  link.download = nombreArchivo;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
